import React from "react";
import { motion } from "framer-motion";

/**
 * FeatureCard – the bottom row of cards present on every page.
 * Two variants:
 *  - "icon-bg": large soft-blue icon background (Home, About, Services, Tech)
 *  - "check": same icon style but with a check badge in the bottom-right (Safety)
 *  - "list": title + description + bullet list with check icons (Technology details)
 */
const FeatureCard = ({
  icon: Icon,
  title,
  description,
  variant = "icon-bg",
  bullets = [],
  index = 0,
  testId,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{
        duration: 0.55,
        delay: 0.08 * index,
        ease: "easeOut",
      }}
      className="lift relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 flex flex-col gap-4"
      data-testid={testId || `feature-card-${index}`}
    >
      <div className="w-14 h-14 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center">
        <Icon size={26} strokeWidth={2} />
      </div>

      <h3 className="text-[1.05rem] font-extrabold text-[#0F1B2D] leading-snug">
        {title}
      </h3>

      {variant === "icon-bg" && (
        <p className="text-[14px] text-slate-600 leading-relaxed">
          {description}
        </p>
      )}

      {variant === "check" && (
        <>
          <p className="text-[14px] text-slate-600 leading-relaxed">
            {description}
          </p>
          <div className="absolute bottom-5 right-5 w-7 h-7 rounded-full bg-[#2563EB] text-white flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12l5 5 9-11"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </>
      )}

      {variant === "list" && (
        <>
          <p className="text-[14px] text-slate-600 leading-relaxed">
            {description}
          </p>
          <ul className="mt-1 space-y-2">
            {bullets.map((b) => (
              <li
                key={b}
                className="flex items-center gap-2 text-[14px] text-slate-700"
              >
                <span className="w-4 h-4 rounded-full bg-blue-50 text-[#2563EB] flex items-center justify-center shrink-0">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12l5 5 9-11"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {b}
              </li>
            ))}
          </ul>
        </>
      )}
    </motion.div>
  );
};

export default FeatureCard;
